import { addLocale, getLocale } from '@umijs/max';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import jaJP from 'antd/locale/ja_JP';
import zhCNMessages from '../locales/zh-CN';
import enUSMessages from '../locales/en-US';
import jaJPMessages from '../locales/ja-JP';
import { getRouteKey } from './menu';

/** showInfo 用的短 locale key */
export type ShowInfoLocaleKey = 'zh' | 'en' | 'ja';

/** umi locale (zh-CN / en-US / ja-JP) → showInfo key (zh / en / ja) */
export const localeToShowInfoKey = (
  locale?: string,
): ShowInfoLocaleKey => {
  const l = (locale || getLocale() || 'zh-CN').toLowerCase();
  if (l.startsWith('en')) return 'en';
  if (l.startsWith('ja')) return 'ja';
  return 'zh';
};

/** showInfo key → 对应的 umi locale 全名 */
export const showInfoKeyToLocale = (
  key: ShowInfoLocaleKey,
): string => {
  if (key === 'en') return 'en-US';
  if (key === 'ja') return 'ja-JP';
  return 'zh-CN';
};

/**
 * 直接从打包内置的 locale 文件（baseMessagesMap）里取消息，
 * 不受 applyMenuDisplayNames 等运行时 addLocale 覆盖影响。
 * 用于"指定 locale 下该菜单的默认翻译"这种查询。
 */
export const getBaseMessage = (
  key: string,
  locale: string,
): string | undefined => {
  const messages = baseMessagesMap[locale];
  return messages?.[key];
};

/**
 * 容错解析 showInfo 字段。后端按 JSON 字符串下发，但偶尔可能直接
 * 返回对象 / null / 坏字符串 —— 这里统一适配。
 *   - object → 直接返回
 *   - JSON string → 尝试解析
 *   - 其他（null/undefined/空串/坏 JSON）→ undefined
 */
export const parseShowInfo = (
  input: unknown,
): { zh?: string; en?: string; ja?: string } | undefined => {
  if (input == null || input === '') return undefined;
  if (typeof input === 'object') {
    return input as { zh?: string; en?: string; ja?: string };
  }
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input);
      return parsed && typeof parsed === 'object' ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const localeMap: Record<string, any> = {
  'zh-CN': zhCN,
  'en-US': enUS,
  'ja-JP': jaJP,
};

const baseMessagesMap: Record<string, Record<string, string>> = {
  'zh-CN': zhCNMessages as Record<string, string>,
  'en-US': enUSMessages as Record<string, string>,
  'ja-JP': jaJPMessages as Record<string, string>,
};

const getMenuLocaleStorageKey = (locale: string, userId?: number | string) =>
  `MENU_LOCALE_OVERRIDES_${userId ?? 'guest'}_${locale}`;

/**
 * 动态更新语言包
 * @param key 国际化 ID
 * @param value 翻译文本
 * @param locale 语言（可选，默认当前语言）
 * @param userId 用户 ID（可选，用于隔离不同用户的覆盖）
 */
export const updateLocale = (key: string, value: string, locale?: string, userId?: number | string) => {
  const currentLocale = locale || getLocale();
  const storageKey = getMenuLocaleStorageKey(currentLocale, userId);

  // 若值与基础翻译一致，清除多余的覆盖缓存，无需 addLocale
  const baseMessages = baseMessagesMap[currentLocale] ?? {};
  if (baseMessages[key] === value) {
    try {
      const cachedOverrides = localStorage.getItem(storageKey);
      if (cachedOverrides) {
        const overrides = JSON.parse(cachedOverrides);
        if (key in overrides) {
          delete overrides[key];
          localStorage.setItem(storageKey, JSON.stringify(overrides));
        }
      }
    } catch (e) {
      console.error('Failed to clean MENU_LOCALE_OVERRIDES', e);
    }
    return;
  }

  // 值与基础翻译不同，保存覆盖
  try {
    const cachedOverrides = localStorage.getItem(storageKey) || '{}';
    const overrides = JSON.parse(cachedOverrides);
    overrides[key] = value;
    localStorage.setItem(storageKey, JSON.stringify(overrides));
  } catch (e) {
    console.error('Failed to save MENU_LOCALE_OVERRIDES', e);
  }

  addLocale(
    currentLocale,
    { [key]: value },
    {
      momentLocale: currentLocale,
      antd: localeMap[currentLocale] || zhCN,
    },
  );
};

/** 清除旧格式的 MENU_LOCALE_OVERRIDES 缓存（无 userId 前缀） */
export const cleanLegacyMenuLocaleCache = () => {
  const locales = ['zh-CN', 'en-US', 'ja-JP'];
  for (const locale of locales) {
    localStorage.removeItem(`MENU_LOCALE_OVERRIDES_${locale}`);
  }
};

/**
 * 把后端返回的菜单 showInfo（租户自定义显示名 - 按 locale 拆分）注入 i18n 包，
 * 让 ProLayout 侧边栏按当前语言显示自定义名。
 *
 * - 后端 showInfo 通过 JSON 字符串下发；这里用 parseShowInfo 统一解析
 * - 仅当 showInfo[locale] 非空时才覆盖；空值保留默认翻译
 * - 仅写内存中的 intl 表，不写 localStorage
 */
export const applyMenuDisplayNames = (menuList: any[] | undefined | null) => {
  if (!Array.isArray(menuList)) return;
  menuList.forEach((item) => {
    const showInfo = parseShowInfo(item?.showInfo);
    const path = item?.path;
    if (!showInfo || !path) return;
    const key = getRouteKey(path, item?.menuName);
    (['zh', 'en', 'ja'] as ShowInfoLocaleKey[]).forEach((shortKey) => {
      const value = showInfo[shortKey];
      if (!value || !value.trim()) return;
      const locale = showInfoKeyToLocale(shortKey);
      addLocale(
        locale,
        { [key]: value },
        {
          momentLocale: locale,
          antd: localeMap[locale] || zhCN,
        },
      );
    });
  });
};

/**
 * 菜单 showInfo 编辑成功后，同步更新缓存：
 * 1) 把 localStorage.user_info.menuList 中对应 menuId 的 showInfo 替换为最新 JSON 串
 *    （供下次冷启动 applyMenuDisplayNames 读到新值）
 * 2) 立即把新的显示名注入运行时 i18n；某 locale 留空时回填该 locale 的默认翻译，
 *    避免之前注入的自定义名残留
 */
export const updateMenuShowInfoCache = (
  menuId: number | string,
  path: string | undefined,
  menuName: string | undefined,
  showInfo: { zh?: string; en?: string; ja?: string } | undefined,
) => {
  const showInfoStr = JSON.stringify(showInfo ?? {});

  try {
    const userInfoStr = localStorage.getItem('user_info');
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      if (Array.isArray(userInfo?.menuList)) {
        const target = userInfo.menuList.find(
          (m: any) => String(m?.menuId) === String(menuId),
        );
        if (target) {
          target.showInfo = showInfoStr;
          localStorage.setItem('user_info', JSON.stringify(userInfo));
        }
      }
    }
  } catch (e) {
    console.error('Failed to patch user_info showInfo cache', e);
  }

  if (!path) return;
  const key = getRouteKey(path, menuName || '');
  (['zh', 'en', 'ja'] as ShowInfoLocaleKey[]).forEach((shortKey) => {
    const locale = showInfoKeyToLocale(shortKey);
    const value = showInfo?.[shortKey];
    const next = value && value.trim() ? value : getBaseMessage(key, locale);
    if (next === undefined) return;
    addLocale(
      locale,
      { [key]: next },
      {
        momentLocale: locale,
        antd: localeMap[locale] || zhCN,
      },
    );
  });
};

/**
 * 初始化菜单国际化
 * 从 localStorage 加载覆盖的菜单名称并应用
 * @param userId 用户 ID（可选，用于隔离不同用户的覆盖）
 */
export const initMenuLocales = (userId?: number | string) => {
  const currentLocale = localStorage.getItem('umi_locale') || 'zh-CN';
  const storageKey = getMenuLocaleStorageKey(currentLocale, userId);

  try {
    const cachedOverrides = localStorage.getItem(storageKey);
    if (cachedOverrides) {
      const overrides = JSON.parse(cachedOverrides);
      if (Object.keys(overrides).length > 0) {
        addLocale(
          currentLocale,
          overrides,
          {
            momentLocale: currentLocale,
            antd: localeMap[currentLocale] || zhCN,
          },
        );
      }
    }
  } catch (e) {
    console.error('Failed to init menu locales', e);
  }
};


